/**
 * Analysis settings and the latest report of a source's monitor — part of the unified intel-source
 * screen (no per-platform screen). The analysis prompt is an IntelAnalysisTemplate; existing templates
 * are listed before the option to write a new one (reuse first). Media analysis (download + transcribe)
 * is offered only when the server reports the source's platform supports it (`mediaCapable`).
 * The latest report is the monitor run's published work-output parts, drawn with the same renderer
 * dashboards use.
 * Template example: NextAdmin `profile/notification/page.tsx` (settings rows with controls).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IntelSourceMonitor, IntelSourceMonitorUpdate } from '../../../generated/api/types.gen.js';
import { zIntelAnalysisTemplateCreate } from '../../../generated/api/zod.gen.js';
import {
  createAnalysisTemplateMutation,
  listAnalysisTemplatesOptions,
  listAnalysisTemplatesQueryKey,
  queryOutputsOptions,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { Button } from '../../../components/ui/Button.js';
import { Callout } from '../../../components/ui/Callout.js';
import { FilterSelect } from '../../../components/ui/FilterSelect.js';
import { SettingRow } from '../../../components/ui/SettingRow.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { Toggle } from '../../../components/ui/Toggle.js';
import { Form, SubmitButton, TextAreaFormField, TextFormField, useZodForm } from '../../../components/form';
import { OutputRenderer } from '../../../components/dashboard/OutputRenderer';

const NEW_PROMPT = '__new__';
const REPORT_KINDS = new Set(['summary', 'report', 'table', 'metric']);

const newPromptSchema = zIntelAnalysisTemplateCreate
  .pick({ promptTemplate: true })
  .extend({ name: zIntelAnalysisTemplateCreate.shape.name.optional() });

interface MonitorAnalysisSettingsProps {
  monitor: IntelSourceMonitor;
  canManage: boolean;
  updating: boolean;
  onUpdate: (patch: IntelSourceMonitorUpdate) => void;
}

export function MonitorAnalysisSettings({ monitor, canManage, updating, onUpdate }: MonitorAnalysisSettingsProps) {
  const platform = monitor.source?.platform ?? '';
  const queryClient = useQueryClient();
  const [writing, setWriting] = useState(false);
  const form = useZodForm(newPromptSchema, { defaultValues: { promptTemplate: '', name: '' } });

  const templatesQuery = useQuery({
    ...listAnalysisTemplatesOptions({ query: { platform } }),
    enabled: platform.length > 0,
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    ...createAnalysisTemplateMutation(),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: listAnalysisTemplatesQueryKey() });
      onUpdate({ analysisTemplateId: created.id });
      setWriting(false);
      form.reset({ promptTemplate: '', name: '' });
    },
  });

  const selected = writing ? NEW_PROMPT : monitor.analysisTemplateId != null ? String(monitor.analysisTemplateId) : '';

  return (
    <>
      <SettingRow
        title="Analysis prompt"
        description={
          templatesQuery.isError
            ? 'The saved prompts did not load, so only the default prompt is listed. Reload the page to pick one.'
            : 'What to pull out of each new item. Pick an existing prompt before writing a new one.'
        }
      >
        <FilterSelect
          label="Analysis prompt"
          value={selected}
          anyLabel="Default prompt"
          options={[
            ...(templatesQuery.data?.data ?? []).map((template) => ({
              value: String(template.id),
              label: template.name ?? 'Untitled prompt',
            })),
            { value: NEW_PROMPT, label: 'Write a new prompt…' },
          ]}
          onChange={(value) => {
            if (value === NEW_PROMPT) {
              setWriting(true);
              return;
            }
            setWriting(false);
            onUpdate({ analysisTemplateId: value === '' ? null : Number(value) });
          }}
          isDisabled={!canManage || updating}
          className="w-64"
        />
      </SettingRow>
      {writing && canManage && (
        <div className="py-5">
          <Form
            form={form}
            className="gap-4"
            onSubmit={(values) =>
              createMutation.mutateAsync({
                body: {
                  platform,
                  category: 'social',
                  name: values.name?.trim() || values.promptTemplate.trim().split(/\s+/).slice(0, 8).join(' '),
                  promptTemplate: values.promptTemplate.trim(),
                  tags: ['media-analysis'],
                },
              })
            }
          >
            <TextAreaFormField
              name="promptTemplate"
              label="Prompt"
              placeholder="What should be pulled out of each item? For example: analyze sentiment, extract strategies or indicators."
              required
            />
            <TextFormField name="name" label="Name" description="Optional. Defaults to the start of the prompt." />
            <div className="flex gap-2">
              <SubmitButton variant="primary" size="md">
                Save prompt
              </SubmitButton>
              <Button variant="ghost" onClick={() => setWriting(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      )}

      {monitor.mediaCapable && (
        <SettingRow
          title="Media analysis"
          description="Downloads each new item, transcribes it and analyzes it with the prompt. Research notes only."
        >
          <Toggle
            checked={monitor.mediaEnabled}
            onChange={(v) => canManage && onUpdate({ mediaEnabled: v })}
            disabled={!canManage || updating}
            aria-label="Media analysis"
          />
        </SettingRow>
      )}
    </>
  );
}

/** The newest monitor run's published parts (summary, metrics, analyzed items, report). */
export function MonitorLatestReport({ latestRunId }: { latestRunId: number | null }) {
  const { data, isLoading, isError, refetch } = useQuery({
    ...queryOutputsOptions({ query: { runId: String(latestRunId ?? ''), limit: '20' } }),
    enabled: latestRunId !== null,
    staleTime: 60_000,
  });
  if (latestRunId === null) return null;
  const parts = (data?.data ?? []).filter((part) => REPORT_KINDS.has(part.kind));

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-heading">Latest report</h3>
      {isLoading ? (
        <Skeleton height={48} />
      ) : isError ? (
        <Callout
          tone="danger"
          title="The latest report did not load"
          actions={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          It may still exist; try again.
        </Callout>
      ) : parts.length === 0 ? (
        <p className="text-sm text-muted">The latest run published no report.</p>
      ) : (
        parts.map((part) => <OutputRenderer key={part.id} output={part} />)
      )}
    </div>
  );
}
